import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { selfServiceAPI, rosterAPI } from '../lib/api';
import { getPhaseLabel, getStatusColor, formatDate } from '../lib/utils';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ClipboardList,
  FileCheck,
  ListTodo,
  ShieldCheck,
  Lock,
  Loader2,
  Upload,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
  CalendarDays,
  MapPin,
  Clock,
  Users,
  ChevronLeft,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const PHASE_ORDER = [
  'gap_analysis', 'application_implementation', 'desktop_nc_closure',
  'pre_assessment', 'final_nc_closure',
];

const PHASE_ICONS = {
  gap_analysis: ClipboardList,
  application_implementation: FileCheck,
  desktop_nc_closure: ListTodo,
  pre_assessment: ShieldCheck,
  final_nc_closure: CheckCircle2,
};

export const ClientPortalPage = () => {
  const [data, setData] = useState(null);
  usePageTitle('Client Portal');
  const [loading, setLoading] = useState(true);
  const [selectedPhaseIdx, setSelectedPhaseIdx] = useState(0);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [evidenceNotes, setEvidenceNotes] = useState('');
  const [ncResponseOpen, setNcResponseOpen] = useState(false);
  const [selectedNC, setSelectedNC] = useState(null);
  const [ncResponse, setNcResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rosterEntries, setRosterEntries] = useState([]);
  const [rosterMonth, setRosterMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [activeTab, setActiveTab] = useState('progress');

  useEffect(() => {
    loadProgress();
  }, []);

  useEffect(() => {
    loadRoster();
  }, [rosterMonth]);

  const loadRoster = async () => {
    try {
      const res = await rosterAPI.getAll({ month: rosterMonth });
      setRosterEntries(res.data);
    } catch { /* client may not have roster entries */ }
  };

  const loadProgress = async () => {
    try {
      const res = await selfServiceAPI.getProgress();
      setData(res.data);
      // Select first in-progress phase
      const activeIdx = res.data.phases.findIndex(p => p.phase.status === 'in_progress');
      if (activeIdx >= 0) setSelectedPhaseIdx(activeIdx);
    } catch (error) {
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEvidence = async () => {
    if (!selectedItem || !evidenceNotes.trim()) return;
    setSubmitting(true);
    try {
      await selfServiceAPI.submitEvidence(selectedItem.id, evidenceNotes);
      toast.success('Evidence submitted successfully!');
      setEvidenceDialogOpen(false);
      setEvidenceNotes('');
      loadProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNCResponse = async () => {
    if (!selectedNC || !ncResponse.trim()) return;
    setSubmitting(true);
    try {
      await selfServiceAPI.submitNCResponse(selectedNC.id, ncResponse);
      toast.success('NC response submitted!');
      setNcResponseOpen(false);
      setNcResponse('');
      loadProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const currentPhase = data.phases[selectedPhaseIdx];

  const navigateRosterMonth = (dir) => {
    const [y, m] = rosterMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setRosterMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const getRosterMonthLabel = () => {
    const [y, m] = rosterMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-in" data-testid="client-portal-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10">
          <h1 className="text-2xl font-heading font-bold">
            {data.client?.organization_name || 'My'} Accreditation Portal
          </h1>
          <p className="text-blue-100">Track progress, view schedule, submit evidence, and respond to NCs</p>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 text-center border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="overall-checklist">
          <p className="text-3xl font-bold text-blue-700">{data.overall.checklist_progress}%</p>
          <p className="text-sm text-blue-500 font-medium">Checklist Progress</p>
          <Progress value={data.overall.checklist_progress} className="mt-2 h-2" />
        </Card>
        <Card className="p-4 text-center border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="overall-tasks">
          <p className="text-3xl font-bold text-emerald-700">{data.overall.task_progress}%</p>
          <p className="text-sm text-emerald-500 font-medium">Task Progress</p>
          <Progress value={data.overall.task_progress} className="mt-2 h-2" />
        </Card>
        <Card className="p-4 text-center border-0 shadow-md bg-gradient-to-br from-violet-50 to-purple-50">
          <p className="text-3xl font-bold text-violet-700">{data.overall.completed_phases}/{data.overall.total_phases}</p>
          <p className="text-sm text-violet-500 font-medium">Phases Completed</p>
        </Card>
        <Card className="p-4 text-center border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50">
          <p className="text-3xl font-bold text-amber-700">{data.client?.current_phase ? getPhaseLabel(data.client.current_phase).split(' ')[0] : '-'}</p>
          <p className="text-sm text-amber-500 font-medium">Current Phase</p>
        </Card>
      </div>

      {/* Main Tabs: Progress | Schedule */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="progress" data-testid="portal-tab-progress">
            <TrendingUp className="w-4 h-4 mr-2" /> Accreditation Progress
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="portal-tab-schedule">
            <CalendarDays className="w-4 h-4 mr-2" /> Planned Schedule ({rosterEntries.length})
          </TabsTrigger>
        </TabsList>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" /> Visit & Consultant Schedule
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateRosterMonth(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[140px] text-center">{getRosterMonthLabel()}</span>
                  <Button variant="outline" size="sm" onClick={() => navigateRosterMonth(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {rosterEntries.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No scheduled visits for {getRosterMonthLabel()}</p>
                  <p className="text-xs mt-1">Your consultant will schedule visits as your accreditation progresses</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rosterEntries.map(entry => (
                    <div key={entry.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-slate-50" data-testid={`client-schedule-${entry.id}`}>
                      <div className={`p-2 rounded-lg shrink-0 ${
                        entry.entry_type === 'client_visit' ? 'bg-blue-100' :
                        entry.entry_type === 'training' ? 'bg-purple-100' : 'bg-slate-100'
                      }`}>
                        {entry.entry_type === 'client_visit' ? <MapPin className="w-4 h-4 text-blue-700" /> :
                         entry.entry_type === 'training' ? <ListTodo className="w-4 h-4 text-purple-700" /> :
                         <CalendarDays className="w-4 h-4 text-slate-700" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{entry.title || entry.entry_type}</p>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" /> {formatDate(entry.date)}
                          </span>
                          {entry.start_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {entry.start_time} - {entry.end_time || ''}
                            </span>
                          )}
                          {entry.consultant_name && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" /> {entry.consultant_name}
                            </span>
                          )}
                        </div>
                        {entry.notes && <p className="text-xs text-muted-foreground mt-1 truncate">{entry.notes}</p>}
                      </div>
                      <Badge className={
                        entry.entry_type === 'client_visit' ? 'bg-blue-100 text-blue-700' :
                        entry.entry_type === 'training' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'
                      }>
                        {entry.entry_type === 'client_visit' ? 'Visit' : entry.entry_type}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress" className="space-y-6">

      {/* Phase Timeline */}
      <Card data-testid="client-phase-timeline">
        <CardContent className="p-4">
          <div className="flex items-center justify-between overflow-x-auto pb-2 gap-1">
            {data.phases.map((phaseData, idx) => {
              const phase = phaseData.phase;
              const Icon = PHASE_ICONS[phase.phase_type] || Circle;
              const isSelected = idx === selectedPhaseIdx;
              const status = phase.status;
              return (
                <React.Fragment key={phase.id}>
                  <button
                    onClick={() => setSelectedPhaseIdx(idx)}
                    className={`flex flex-col items-center gap-1 min-w-[110px] p-3 rounded-lg transition-all ${
                      isSelected ? 'bg-primary/10 ring-2 ring-primary' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`client-phase-btn-${phase.phase_type}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      status === 'completed' ? 'bg-green-100 text-green-700' :
                      status === 'in_progress' ? 'bg-primary/20 text-primary' :
                      status === 'blocked' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> :
                       status === 'blocked' ? <AlertTriangle className="w-4 h-4" /> :
                       status === 'pending' ? <Lock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className="text-xs font-medium text-center leading-tight">{getPhaseLabel(phase.phase_type)}</span>
                    <div className="flex items-center gap-1">
                      <Progress value={phaseData.checklist_total > 0 ? (phaseData.checklist_completed / phaseData.checklist_total) * 100 : 0} className="w-12 h-1.5" />
                      <span className="text-[10px] text-muted-foreground">{phaseData.checklist_completed}/{phaseData.checklist_total}</span>
                    </div>
                  </button>
                  {idx < data.phases.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Phase Detail */}
      {currentPhase && (
        <Tabs defaultValue="checklist">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="checklist" data-testid="tab-checklist">
              <ClipboardList className="w-4 h-4 mr-2" /> Checklist ({currentPhase.checklist_completed}/{currentPhase.checklist_total})
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Tasks ({currentPhase.tasks_completed}/{currentPhase.tasks_total})
            </TabsTrigger>
            <TabsTrigger value="ncs" data-testid="tab-ncs">
              <AlertTriangle className="w-4 h-4 mr-2" /> NCs ({currentPhase.ncs.length})
            </TabsTrigger>
          </TabsList>

          {/* Checklist Tab */}
          <TabsContent value="checklist" className="space-y-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{getPhaseLabel(currentPhase.phase.phase_type)} Checklist</CardTitle>
                  <Badge className={getStatusColor(currentPhase.phase.status)}>
                    {currentPhase.phase.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <Progress value={currentPhase.checklist_total > 0 ? (currentPhase.checklist_completed / currentPhase.checklist_total) * 100 : 0} className="mt-2" />
              </CardHeader>
              <CardContent>
                {currentPhase.checklist.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No checklist items for this phase yet</p>
                ) : (
                  <div className="space-y-2">
                    {currentPhase.checklist.map((item) => (
                      <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border ${item.is_completed ? 'bg-green-50/50 border-green-200' : ''}`} data-testid={`client-checklist-${item.id}`}>
                        {item.is_completed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${item.is_completed ? 'line-through text-muted-foreground' : ''}`}>{item.title}</p>
                          {item.description && <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>}
                          {item.notes && (
                            <div className="mt-1 p-2 bg-blue-50 rounded text-xs text-blue-700">
                              Evidence: {item.notes}
                            </div>
                          )}
                          {item.evidence_submitted_at && (
                            <p className="text-xs text-green-600 mt-1">Evidence submitted {formatDate(item.evidence_submitted_at)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          {!item.is_completed && currentPhase.phase.status === 'in_progress' && (
                            <Button size="sm" variant="outline" onClick={() => {
                              setSelectedItem(item);
                              setEvidenceNotes(item.notes || '');
                              setEvidenceDialogOpen(true);
                            }} data-testid={`submit-evidence-${item.id}`}>
                              <Upload className="w-3 h-3 mr-1" /> Evidence
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                {currentPhase.tasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No tasks for this phase</p>
                ) : (
                  <div className="space-y-2">
                    {currentPhase.tasks.map((task) => (
                      <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg border">
                        {task.status === 'completed' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{task.title}</p>
                          {task.description && <p className="text-xs text-muted-foreground">{task.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{task.priority}</Badge>
                          <Badge className={getStatusColor(task.status)}>{task.status?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* NCs Tab */}
          <TabsContent value="ncs">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Non-Conformities</CardTitle>
              </CardHeader>
              <CardContent>
                {currentPhase.ncs.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-2" />
                    <p className="text-sm text-muted-foreground">No non-conformities for this phase</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentPhase.ncs.map((nc) => (
                      <div key={nc.id} className="p-4 rounded-lg border space-y-2" data-testid={`nc-item-${nc.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{nc.title}</p>
                          <Badge className={getStatusColor(nc.status)}>{nc.status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{nc.description}</p>
                        {nc.client_response && (
                          <div className="p-2 bg-blue-50 rounded text-sm text-blue-700">
                            <p className="font-medium text-xs mb-1">Your Response:</p>
                            {nc.client_response}
                          </div>
                        )}
                        {nc.status !== 'closed' && (
                          <Button size="sm" variant="outline" onClick={() => {
                            setSelectedNC(nc);
                            setNcResponse(nc.client_response || '');
                            setNcResponseOpen(true);
                          }} data-testid={`respond-nc-${nc.id}`}>
                            <MessageSquare className="w-3 h-3 mr-1" /> {nc.client_response ? 'Update Response' : 'Submit Response'}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Evidence Submission Dialog */}
      <Dialog open={evidenceDialogOpen} onOpenChange={setEvidenceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Evidence</DialogTitle>
            <DialogDescription>
              Provide evidence or notes for: {selectedItem?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Evidence Notes *</Label>
              <Textarea
                value={evidenceNotes}
                onChange={(e) => setEvidenceNotes(e.target.value)}
                rows={4}
                placeholder="Describe the evidence, reference documents, or provide detailed notes..."
                data-testid="evidence-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvidenceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitEvidence} disabled={submitting || !evidenceNotes.trim()} data-testid="submit-evidence-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Submit Evidence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NC Response Dialog */}
      <Dialog open={ncResponseOpen} onOpenChange={setNcResponseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Non-Conformity</DialogTitle>
            <DialogDescription>
              NC: {selectedNC?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedNC?.description && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">{selectedNC.description}</div>
            )}
            <div className="space-y-2">
              <Label>Your Response *</Label>
              <Textarea
                value={ncResponse}
                onChange={(e) => setNcResponse(e.target.value)}
                rows={4}
                placeholder="Describe corrective actions taken, reference evidence documents..."
                data-testid="nc-response-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNcResponseOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitNCResponse} disabled={submitting || !ncResponse.trim()} data-testid="submit-nc-response-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Submit Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientPortalPage;
